import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Search, Star, X } from 'lucide-react';
import type { Project, Task } from '@foodstyles/shared';
import { useBootstrap } from '../hooks/useBootstrap';
import { useToggleFavorite } from '../hooks/useFavorites';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../lib/cn';
import { inputCls } from './primitives';

// Clockify-style picker: browse projects (collapsed; tap to reveal their tasks)
// or type to search tasks/projects flat. Favorited projects & tasks are pinned
// to the top. Picking a task auto-fills its project. The prop contract is
// unchanged from the old task-first combobox so every caller keeps working;
// favorites are read from the cached bootstrap and toggled in place.
interface Props {
  projects: Project[];
  tasks: Task[];
  projectId: string | null;
  taskId: string | null;
  invalid?: boolean;
  disabled?: boolean;
  // Fallback label for a selected task that's no longer in `tasks` (e.g. archived
  // after the entry was created), so the field never shows blank for a real pick.
  selectedLabel?: string;
  onPick: (v: { projectId: string; taskId: string }) => void;
  // Focus + open the dropdown on mount (for inline "click to edit" usage).
  autoFocus?: boolean;
  // Fired when the dropdown closes without a pick (click-outside / Escape), so an
  // inline caller can exit its edit mode.
  onClose?: () => void;
}

type Row =
  | { kind: 'clear'; key: string }
  | { kind: 'header'; key: string; label: string }
  | { kind: 'project'; key: string; p: Project; count: number; fav: boolean }
  | { kind: 'task'; key: string; t: Task; p: Project; fav: boolean; showProject: boolean }
  | { kind: 'empty'; key: string };

const isSelectable = (r: Row) => r.kind === 'clear' || r.kind === 'project' || r.kind === 'task';

function StarBtn({
  on,
  onToggle,
  label,
  className,
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={on}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        'shrink-0 rounded p-1 transition-colors',
        on ? 'text-amber-400 hover:text-amber-500' : 'text-slate-300 dark:text-slate-600 hover:text-amber-400',
        className,
      )}
    >
      <Star size={15} fill={on ? 'currentColor' : 'none'} />
    </button>
  );
}

export function TaskCombobox({
  projects,
  tasks,
  projectId,
  taskId,
  invalid,
  disabled,
  selectedLabel,
  onPick,
  autoFocus,
  onClose,
}: Props) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const boxRef = useClickOutside<HTMLDivElement>(() => {
    setOpen(false);
    setQ('');
    onClose?.();
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Inline "click to edit": focus on mount, which opens the dropdown via onFocus.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optId = (i: number) => `${baseId}-opt-${i}`;

  const boot = useBootstrap();
  const toggleFav = useToggleFavorite();
  const favProjects = useMemo(() => new Set(boot.data?.favoriteProjectIds ?? []), [boot.data]);
  const favTasks = useMemo(() => new Set(boot.data?.favoriteTaskIds ?? []), [boot.data]);

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );
  const tasksByProject = useMemo(() => {
    const m = new Map<string, Task[]>();
    for (const t of tasks) {
      const arr = m.get(t.projectId);
      if (arr) arr.push(t);
      else m.set(t.projectId, [t]);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [tasks]);

  const selTask = tasks.find((t) => t.id === taskId);
  const selProject = selTask ? projectById.get(selTask.projectId) ?? null : null;
  // Closed-field label: show "Project · Task" for the live selection, fall back to
  // the caller's label for a task that's no longer visible (e.g. archived).
  const closedLabel = selTask
    ? selProject
      ? `${selProject.name} · ${selTask.name}`
      : selTask.name
    : taskId
      ? selectedLabel ?? ''
      : '';

  // Reset browse state each time the dropdown opens: collapsed by default, with
  // the currently-selected task's project pre-expanded so the selection shows.
  useEffect(() => {
    if (open) setExpanded(new Set(projectId ? [projectId] : []));
  }, [open, projectId]);
  useEffect(() => setHi(0), [q, open]);

  // ── Build the visible rows (single source of truth for render + keyboard) ─────
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    const needle = q.trim().toLowerCase();

    if (taskId) out.push({ kind: 'clear', key: 'clear' });

    if (needle) {
      // Search: flat, ranked task list (task- or project-name match).
      const matches = tasks
        .map((t) => ({ t, p: projectById.get(t.projectId) }))
        .filter(
          (x): x is { t: Task; p: Project } =>
            !!x.p && (x.t.name.toLowerCase().includes(needle) || x.p.name.toLowerCase().includes(needle)),
        )
        .sort((a, b) => {
          const score = (n: string) => (n.startsWith(needle) ? 0 : n.includes(needle) ? 1 : 2);
          return score(a.t.name.toLowerCase()) - score(b.t.name.toLowerCase());
        })
        .slice(0, 20);
      for (const x of matches) {
        out.push({ kind: 'task', key: `s-${x.t.id}`, t: x.t, p: x.p, fav: favTasks.has(x.t.id), showProject: true });
      }
      return out;
    }

    // Browse: pinned favorites, then all projects (collapsed).
    const favP = sortedProjects.filter((p) => favProjects.has(p.id));
    const favT = tasks.filter((t) => favTasks.has(t.id) && projectById.has(t.projectId));
    if (favP.length || favT.length) {
      out.push({ kind: 'header', key: 'h-fav', label: 'Favorites' });
      for (const p of favP) {
        out.push({ kind: 'project', key: `fav-${p.id}`, p, count: tasksByProject.get(p.id)?.length ?? 0, fav: true });
        if (expanded.has(p.id)) {
          const ts = tasksByProject.get(p.id) ?? [];
          if (ts.length === 0) out.push({ kind: 'empty', key: `fav-${p.id}-empty` });
          for (const t of ts) out.push({ kind: 'task', key: `fav-${p.id}-${t.id}`, t, p, fav: favTasks.has(t.id), showProject: false });
        }
      }
      for (const t of favT) {
        const p = projectById.get(t.projectId)!;
        out.push({ kind: 'task', key: `favtask-${t.id}`, t, p, fav: true, showProject: true });
      }
    }

    out.push({ kind: 'header', key: 'h-all', label: favP.length || favT.length ? 'All projects' : 'Projects' });
    for (const p of sortedProjects) {
      out.push({ kind: 'project', key: `all-${p.id}`, p, count: tasksByProject.get(p.id)?.length ?? 0, fav: favProjects.has(p.id) });
      if (expanded.has(p.id)) {
        const ts = tasksByProject.get(p.id) ?? [];
        if (ts.length === 0) out.push({ kind: 'empty', key: `all-${p.id}-empty` });
        for (const t of ts) out.push({ kind: 'task', key: `all-${p.id}-${t.id}`, t, p, fav: favTasks.has(t.id), showProject: false });
      }
    }
    return out;
  }, [q, taskId, tasks, sortedProjects, projectById, tasksByProject, expanded, favProjects, favTasks]);

  const selectableIdx = useMemo(() => rows.flatMap((r, i) => (isSelectable(r) ? [i] : [])), [rows]);
  const activeRow = selectableIdx[Math.min(hi, selectableIdx.length - 1)] ?? -1;

  // Keep the stored highlight in range when the selectable set shrinks (collapse
  // / unpin), so ArrowUp doesn't have to walk a stale-large index back into view.
  useEffect(() => {
    setHi((h) => Math.min(h, Math.max(0, selectableIdx.length - 1)));
  }, [selectableIdx.length]);
  // Scroll the arrow-highlighted row into view in the (possibly long) browse list.
  useEffect(() => {
    if (open) listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeRow, open]);

  const pick = (p: Project, t: Task) => {
    onPick({ projectId: p.id, taskId: t.id });
    setOpen(false);
    setQ('');
  };
  const clear = () => {
    onPick({ projectId: projectId ?? '', taskId: '' });
    setOpen(false);
    setQ('');
  };
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const activate = (row: Row) => {
    if (row.kind === 'clear') clear();
    else if (row.kind === 'project') toggleExpand(row.p.id);
    else if (row.kind === 'task') pick(row.p, row.t);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => Math.min(h + 1, selectableIdx.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[activeRow];
      if (row) activate(row);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQ('');
      onClose?.();
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input
          ref={inputRef}
          className={cn(inputCls, open && 'pl-9', invalid && 'border-rose-400 dark:border-rose-500 ring-2 ring-rose-200 dark:ring-rose-500/30')}
          placeholder={open ? 'Search task or project…' : 'Select a task…'}
          value={open ? q : closedLabel}
          disabled={disabled}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (!disabled) {
              setOpen(true);
              setQ('');
            }
          }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-label="Task"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={open && activeRow >= 0 ? optId(activeRow) : undefined}
        />
        {open && <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      </div>

      {open && (
        <div
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label="Tasks and projects"
          // Keep focus in the input while clicking rows/stars so keyboard nav survives.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1 shadow-lg lg:min-w-[340px]"
        >
          {rows.map((row, i) => {
            if (row.kind === 'header') {
              return (
                <p key={row.key} className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {row.label}
                </p>
              );
            }
            if (row.kind === 'empty') {
              return (
                <p key={row.key} className="py-1 pl-9 pr-3 text-xs italic text-slate-400">
                  No tasks
                </p>
              );
            }
            if (row.kind === 'clear') {
              return (
                <div
                  key={row.key}
                  id={optId(i)}
                  role="option"
                  aria-selected={i === activeRow}
                  data-active={i === activeRow}
                  onMouseEnter={() => setHi(selectableIdx.indexOf(i))}
                  onClick={clear}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 border-b border-slate-100 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-slate-400',
                    i === activeRow && 'bg-[#FF5A3C]/[0.06] dark:bg-[#FF5A3C]/10',
                  )}
                >
                  <X size={13} /> Clear task
                </div>
              );
            }
            if (row.kind === 'project') {
              const isOpen = expanded.has(row.p.id);
              return (
                <div
                  key={row.key}
                  id={optId(i)}
                  role="option"
                  aria-selected={i === activeRow}
                  aria-expanded={isOpen}
                  data-active={i === activeRow}
                  onMouseEnter={() => setHi(selectableIdx.indexOf(i))}
                  onClick={() => toggleExpand(row.p.id)}
                  className={cn(
                    'flex cursor-pointer items-center gap-1.5 px-2 py-2 text-sm',
                    i === activeRow && 'bg-[#FF5A3C]/[0.06] dark:bg-[#FF5A3C]/10',
                  )}
                >
                  <span className="shrink-0 text-slate-400">
                    {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </span>
                  <span className="truncate font-semibold text-slate-700 dark:text-slate-200">{row.p.name}</span>
                  <span className="ml-auto shrink-0 text-xs text-slate-400">
                    {row.count} {row.count === 1 ? 'task' : 'tasks'}
                  </span>
                  <StarBtn
                    on={row.fav}
                    label={row.fav ? `Unpin ${row.p.name}` : `Pin ${row.p.name}`}
                    onToggle={() => toggleFav.mutate({ projectId: row.p.id, on: !row.fav })}
                  />
                </div>
              );
            }
            // task
            return (
              <div
                key={row.key}
                id={optId(i)}
                role="option"
                aria-selected={i === activeRow}
                data-active={i === activeRow}
                onMouseEnter={() => setHi(selectableIdx.indexOf(i))}
                onClick={() => pick(row.p, row.t)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 py-2 pr-2 text-sm',
                  row.showProject ? 'pl-3' : 'pl-9',
                  i === activeRow && 'bg-[#FF5A3C]/[0.06] dark:bg-[#FF5A3C]/10',
                )}
              >
                <span className="truncate text-slate-800 dark:text-slate-100">{row.t.name}</span>
                {row.showProject && <span className="ml-auto shrink-0 text-xs text-slate-400">{row.p.name}</span>}
                <StarBtn
                  on={row.fav}
                  className={row.showProject ? undefined : 'ml-auto'}
                  label={row.fav ? `Unpin ${row.t.name}` : `Pin ${row.t.name}`}
                  onToggle={() => toggleFav.mutate({ taskId: row.t.id, on: !row.fav })}
                />
              </div>
            );
          })}
          {!rows.some((r) => r.kind === 'task' || r.kind === 'project') && (
            <p className="px-3 py-3 text-center text-xs text-slate-400">
              {q.trim() ? `No matches for “${q}”.` : 'No projects available.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
